import React, { useMemo, useState } from 'react'
import gql from 'graphql-tag'
import { useQuery } from '@apollo/client'
import Skeleton from 'react-loading-skeleton'
import moment from 'moment-timezone'
import { Input } from 'reactstrap'
import HistoryTable from './HistoryTable'
import './HistoryList.css'

const BQHistoryQuery = gql`
    query BQHistoryQuery($objectIds: [ID!]!, $startDate: Date, $endDate: Date) {
        bqHistory(objectIds: $objectIds, startDate: $startDate, endDate: $endDate) {
            items {
                appLabel
                modelName
                objectId
                eventTimestamp
                actorName
                isNew
                isDeleted
                snapshot
                dirtyFields
                comment
            }
        }
    }
`

const MAX_HISTORY_RANGE_DAYS = 90
const ISO = moment.HTML5_FMT.DATE

// One list, so choosing a window is one click and every option is visible at
// once: the recent windows people usually want, then named months for the
// "what happened last March" case that used to mean typing two dates.
const RANGE_PRESETS = [7, 30, MAX_HISTORY_RANGE_DAYS]
const MONTHS_OFFERED = 24
const DEFAULT_RANGE_VALUE = 'last:7'

const lastDays = (days) => ({
    start: moment().subtract(days - 1, 'days').format(ISO),
    end: moment().format(ISO),
})

// A whole calendar month, clamped so the month still running does not ask for
// days that have not happened. Always well inside the window cap.
const monthRange = (month) => ({
    start: moment(month, 'YYYY-MM').startOf('month').format(ISO),
    end: moment.min(moment(month, 'YYYY-MM').endOf('month'), moment()).format(ISO),
})

const rangeFromValue = (value) => {
    const [kind, argument] = String(value).split(':')
    return kind === 'month' ? monthRange(argument) : lastDays(Number(argument))
}

const monthOptions = () => (
    Array.from({ length: MONTHS_OFFERED }, (_, index) => {
        const month = moment().subtract(index, 'months')
        return { value: `month:${month.format('YYYY-MM')}`, label: month.format('MMMM YYYY') }
    })
)

function BQHistoryList({ objectIds = [], emptyText = 'No history found yet.', subject = null }) {
    // The chosen option *is* the range -- every option yields a valid one, so
    // there is nothing to validate, no draft, and no Apply.
    const [rangeValue, setRangeValue] = useState(DEFAULT_RANGE_VALUE)
    const range = useMemo(() => rangeFromValue(rangeValue), [rangeValue])
    const normalizedObjectIds = useMemo(
        () => (objectIds || []).filter(Boolean),
        [objectIds]
    )
    const isDefaultDateRange = rangeValue === DEFAULT_RANGE_VALUE

    const { data, loading, error } = useQuery(BQHistoryQuery, {
        variables: {
            objectIds: normalizedObjectIds,
            startDate: range.start,
            endDate: range.end,
        },
        skip: !normalizedObjectIds.length,
        fetchPolicy: 'cache-and-network',
    })

    const header = (
        <div className="vgm-audit-header">
            <div className="vgm-audit-header-titles">
                <h2 className="vgm-audit-title">Activity Logs</h2>
                <p className="vgm-audit-subtitle">
                    {subject ? `Every recorded change to ${subject}` : 'Every recorded change'}, who made it and when.
                </p>
            </div>
            <div className="vgm-audit-daterange">
                <label className="vgm-audit-range-label" htmlFor="bq-history-range">Showing</label>
                <Input
                    id="bq-history-range"
                    type="select"
                    className="vgm-audit-range-select"
                    value={rangeValue}
                    disabled={loading}
                    onChange={(e) => setRangeValue(e.target.value)}
                >
                    {RANGE_PRESETS.map((days) => (
                        <option key={days} value={`last:${days}`}>Last {days} days</option>
                    ))}
                    <optgroup label="By month">
                        {monthOptions().map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </optgroup>
                </Input>
            </div>
        </div>
    )

    const historyItems = data?.bqHistory?.items || []

    if (loading && !historyItems.length) {
        return (
            <div className="vgm-audit-trail">
                {header}
                <Skeleton height={90} count={4} className="mb-2" />
            </div>
        )
    }

    if (error) {
        return (
            <div className="vgm-audit-trail">
                {header}
                <div className="alert alert-danger mb-0">{error.message}</div>
            </div>
        )
    }

    if (!historyItems.length) {
        return (
            <div className="vgm-audit-trail">
                {header}
                <p className="vgm-audit-empty">
                    {isDefaultDateRange
                        ? 'No history found in the last 7 days. Try expanding the date range.'
                        : emptyText}
                </p>
            </div>
        )
    }

    return (
        <div className="vgm-audit-trail">
            {header}
            <HistoryTable
                key={`${normalizedObjectIds.join('|')}|${range.start}|${range.end}`}
                items={historyItems}
            />
        </div>
    )
}

export default BQHistoryList
